import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { systemApi } from '@/lib/api'
import { Loader2, Bell, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'

export function WarningMessagePage() {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [projectId, setProjectId] = useState<string | undefined>(undefined)
  const pageSize = 20

  const loadMessages = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await systemApi.getWarningArticle(page, undefined, projectId)
      const list = res?.data || res || []
      setMessages(Array.isArray(list) ? list : [])
      setTotal(res?.total || 0)
    } catch (err) {
      console.error('Failed to load warning messages:', err)
    } finally {
      setLoading(false)
    }
  }, [page, projectId])

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  const emotionBadge = (emotion: number) => {
    if (emotion === 1) return { variant: 'success' as const, text: 'positive' }
    if (emotion === -1) return { variant: 'destructive' as const, text: 'negative' }
    return { variant: 'secondary' as const, text: 'neutral' }
  }

  const handleProjectFilter = (pid: string) => {
    setProjectId(pid || undefined)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Bell className="h-5 w-5" />
          {t('settings.warning.messageList')}
        </h2>
        <div className="flex items-center gap-2">
          <select
            className="h-8 rounded-md border border-input bg-background px-3 text-xs"
            value={projectId || ''}
            onChange={(e) => handleProjectFilter(e.target.value)}
          >
            <option value="">{t('common.all')}</option>
            <option value="2001">华为品牌口碑监测</option>
            <option value="2002">小米产品评价监测</option>
            <option value="2003">苹果动态追踪</option>
            <option value="2004">人工智能行业监测</option>
            <option value="2005">新能源行业监测</option>
          </select>
          <Badge variant="secondary">{t('common.total')} {total}</Badge>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">{t('monitor.articleTitle')}</TableHead>
                <TableHead>{t('monitor.sourceName')}</TableHead>
                <TableHead>{t('monitor.publishTime')}</TableHead>
                <TableHead>{t('monitor.emotion')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {t('common.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((msg) => {
                  const badge = emotionBadge(msg.emotionalIndex ?? msg.emotion)
                  return (
                    <TableRow key={msg.article_public_id || msg.id}>
                      <TableCell className="font-medium">
                        <a
                          href={msg.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary hover:underline"
                          onClick={(e) => {
                            if (!msg.url || msg.url?.startsWith('https://example.com')) {
                              e.preventDefault()
                              alert('此为模拟数据，原文链接不可访问。连接后端后将跳转到真实原文。')
                            }
                          }}
                        >
                          {msg.title}
                        </a>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{msg.source_name}</TableCell>
                      <TableCell className="text-muted-foreground">{msg.publish_time}</TableCell>
                      <TableCell>
                        <Badge variant={badge.variant}>
                          {t(`monitor.${badge.text}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={msg.url} target="_blank" rel="noopener noreferrer" onClick={(e) => {
                            if (!msg.url || msg.url?.startsWith('https://example.com')) {
                              e.preventDefault()
                              alert('此为模拟数据，原文链接不可访问。连接后端后将跳转到真实原文。')
                            }
                          }}>
                            <ExternalLink className="mr-1 h-3 w-3" />
                            {t('common.viewOriginal') || '查看原文'}
                          </a>
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('common.total')} {total} {t('common.items')}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
              {t('common.prev')}
            </Button>
            <span className="text-sm">
              {t('common.page')} {page} {t('common.of')} {Math.ceil(total / pageSize)}
            </span>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage(page + 1)}>
              {t('common.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
